import { useState, useEffect, useCallback } from 'react';
import {
  institutionWorkflows,
  institutionUsers,
  type WorkflowTask,
  type User,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/common/Select';
import styles from './TasksPage.module.css';

type TaskStatus = 'all' | 'pending' | 'approved' | 'rejected';

export function TasksPage() {
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<TaskStatus>('pending');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Action modal state
  const [selectedTask, setSelectedTask] = useState<WorkflowTask | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'delegate'>('approve');
  const [comments, setComments] = useState('');
  const [delegateTo, setDelegateTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params: { page: number; perPage: number; status?: string } = {
        page,
        perPage: 20,
      };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await institutionWorkflows.getTasks(params);
      if (response.success && response.data) {
        setTasks(response.data);
        if (response.meta) {
          setTotalPages(response.meta.totalPages);
        }
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await institutionUsers.list({ perPage: 100 });
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  function openActionModal(task: WorkflowTask, action: 'approve' | 'reject' | 'delegate') {
    setSelectedTask(task);
    setActionType(action);
    setComments('');
    setDelegateTo('');
    setError(null);
    setShowActionModal(true);
  }

  async function handleAction() {
    if (!selectedTask) return;

    if (actionType === 'delegate' && !delegateTo) {
      setError('Please select a user to delegate to');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await institutionWorkflows.performAction(selectedTask.id, {
        action: actionType,
        comments: comments || undefined,
        delegateTo: actionType === 'delegate' ? delegateTo : undefined,
      });

      if (response.success) {
        setShowActionModal(false);
        fetchTasks();
      } else {
        setError(response.error?.message || 'Failed to perform action');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return 'No deadline';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function formatDateTime(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'pending':
        return styles.statusPending;
      case 'approved':
        return styles.statusApproved;
      case 'rejected':
        return styles.statusRejected;
      case 'delegated':
        return styles.statusDelegated;
      default:
        return '';
    }
  }

  function isOverdue(dueAt: string | null): boolean {
    if (!dueAt) return false;
    return new Date(dueAt) < new Date();
  }

  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>My Tasks</h1>
          <p className={styles.subtitle}>Workflow tasks assigned to you</p>
        </div>
        {pendingCount > 0 && (
          <div className={styles.pendingBadge}>
            {pendingCount} pending
          </div>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.statusTabs}>
          {(['all', 'pending', 'approved', 'rejected'] as TaskStatus[]).map((status) => (
            <button
              key={status}
              className={`${styles.statusTab} ${filterStatus === status ? styles.active : ''}`}
              onClick={() => {
                setFilterStatus(status);
                setPage(1);
              }}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.tasksContainer}>
        {loading ? (
          <div className={styles.loading}>Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <p>No {filterStatus === 'all' ? '' : filterStatus} tasks found</p>
            {filterStatus !== 'all' && (
              <Button variant="secondary" onClick={() => setFilterStatus('all')}>
                View All Tasks
              </Button>
            )}
          </div>
        ) : (
          <div className={styles.tasksList}>
            {tasks.map((task) => (
              <div
                key={task.id}
                className={`${styles.taskCard} ${isOverdue(task.dueAt) && task.status === 'pending' ? styles.overdue : ''}`}
              >
                <div className={styles.taskHeader}>
                  <div className={styles.taskInfo}>
                    <h3 className={styles.taskTitle}>{task.document.title}</h3>
                    {task.document.referenceNumber && (
                      <span className={styles.taskRef}>{task.document.referenceNumber}</span>
                    )}
                  </div>
                  <span className={`${styles.statusBadge} ${getStatusBadgeClass(task.status)}`}>
                    {task.status}
                  </span>
                </div>

                <div className={styles.taskDetails}>
                  <div className={styles.taskDetail}>
                    <span className={styles.detailLabel}>Action Required</span>
                    <span className={styles.detailValue}>{task.step.actionRequired}</span>
                  </div>
                  <div className={styles.taskDetail}>
                    <span className={styles.detailLabel}>Step</span>
                    <span className={styles.detailValue}>{task.step.name}</span>
                  </div>
                  <div className={styles.taskDetail}>
                    <span className={styles.detailLabel}>Due Date</span>
                    <span className={`${styles.detailValue} ${isOverdue(task.dueAt) && task.status === 'pending' ? styles.overdueText : ''}`}>
                      {formatDate(task.dueAt)}
                    </span>
                  </div>
                  <div className={styles.taskDetail}>
                    <span className={styles.detailLabel}>Received</span>
                    <span className={styles.detailValue}>{formatDateTime(task.createdAt)}</span>
                  </div>
                </div>

                {task.status === 'pending' && (
                  <div className={styles.taskActions}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openActionModal(task, 'approve')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Approve
                    </Button>
                    {task.step.allowReject && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openActionModal(task, 'reject')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        Reject
                      </Button>
                    )}
                    {task.step.allowDelegate && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openActionModal(task, 'delegate')}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        Delegate
                      </Button>
                    )}
                  </div>
                )}

                {task.status !== 'pending' && task.completedAt && (
                  <div className={styles.taskCompleted}>
                    <span className={styles.completedLabel}>
                      {task.status === 'approved' ? 'Approved' : task.status === 'rejected' ? 'Rejected' : 'Delegated'} on
                    </span>
                    <span className={styles.completedDate}>{formatDateTime(task.completedAt)}</span>
                    {task.comments && (
                      <p className={styles.taskComments}>"{task.comments}"</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal}
        onClose={() => setShowActionModal(false)}
        title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Task`}
      >
        <div className={styles.actionModal}>
          {error && <div className={styles.error}>{error}</div>}

          {selectedTask && (
            <div className={styles.taskSummary}>
              <h4>{selectedTask.document.title}</h4>
              <p>Step: {selectedTask.step.name}</p>
              <p>Action: {selectedTask.step.actionRequired}</p>
            </div>
          )}

          {actionType === 'delegate' && (
            <Select
              label="Delegate To"
              value={delegateTo}
              onChange={(e) => setDelegateTo(e.target.value)}
              required
            >
              <option value="">Select a user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} - {user.department?.name || 'No Department'}
                </option>
              ))}
            </Select>
          )}

          <div className={styles.commentsField}>
            <label className={styles.commentsLabel}>
              Comments {actionType === 'reject' ? '(Required)' : '(Optional)'}
            </label>
            <textarea
              className={styles.commentsTextarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={
                actionType === 'approve'
                  ? 'Add any comments about your approval...'
                  : actionType === 'reject'
                  ? 'Please provide a reason for rejection...'
                  : 'Add any instructions for the delegate...'
              }
              rows={4}
              required={actionType === 'reject'}
            />
          </div>

          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            <Button
              variant={actionType === 'reject' ? 'danger' : 'primary'}
              onClick={handleAction}
              disabled={submitting || (actionType === 'reject' && !comments)}
            >
              {submitting ? 'Processing...' : `Confirm ${actionType}`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
